<script setup lang="ts">
import { usePejer } from '@/Pejer'
import { Landmark } from 'lucide-vue-next'
import DataStateButton from 'pejer/src/components/DataState/DataStateButton.vue'
import { Button } from 'pejer/src/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from 'pejer/src/components/ui/dialog'
import DialogFooter from 'pejer/src/components/ui/dialog/DialogFooter.vue'
import { Input } from 'pejer/src/components/ui/input'
import { Label } from 'pejer/src/components/ui/label'
import usePejerMutation from 'pejer/src/usePejerMutation'
import usePejerQuery from 'pejer/src/usePejerQuery'
import { computed, ref, watch } from 'vue'
import ClubAccountHistoryList from './ClubAccountHistoryList.vue'
import { ClubAccountItem, formatKroner, toOere } from './types'

const props = defineProps<{
  item: ClubAccountItem
}>()

const isOpen = ref(false)
const showHistory = computed(() => isOpen.value && props.item.paidKroner > 0)
const pejer = usePejer()

// Partially paid: show what has already come in.
const { data: history, isLoading: historyLoading } = usePejerQuery(
  pejer.urls.payments['club-account-history'],
  {
    pathArgs: { id: props.item.paymentReferenceID },
    queryKey: ['club-account-history', props.item.paymentReferenceID],
    enabled: showHistory,
  }
)

const remainingKroner = computed(() => Math.max(0, props.item.grossKroner - props.item.paidKroner))
const amount = ref<string | number>('')
const paidAt = ref('')
const fromAccount = ref('')
const note = ref('')

watch(isOpen, (open) => {
  if (!open) return
  amount.value = remainingKroner.value
  paidAt.value = new Date().toISOString().slice(0, 10)
  fromAccount.value = ''
  note.value = ''
})

const amountInOere = computed(() => toOere(amount.value))
const fromAccountDigits = computed(() => fromAccount.value.replace(/\D/g, ''))
const isValid = computed(
  () =>
    amountInOere.value >= 1 &&
    amountInOere.value <= toOere(remainingKroner.value) &&
    (fromAccountDigits.value.length === 0 || fromAccountDigits.value.length === 11)
)

const { mutate, dataState } = usePejerMutation(
  pejer.urls.payments['club-account-register-payment'],
  {
    queryKeys: [['payments'], ['invoices'], ['club-account-history']],
    onSuccess: () => {
      isOpen.value = false
    },
  },
  { toastOnSuccess: { title: 'Innbetalingen er registrert' } }
)

function submit() {
  if (!isValid.value) return
  mutate({
    pathArgs: { id: props.item.paymentReferenceID },
    body: {
      amount: amountInOere.value,
      paidAt: paidAt.value ? new Date(paidAt.value).toISOString() : null,
      fromBankAccountNumber: fromAccountDigits.value || null,
      note: note.value || null,
    },
  })
}
</script>
<template>
  <Dialog v-model:open="isOpen">
    <DialogTrigger as-child>
      <slot />
    </DialogTrigger>
    <DialogContent class="max-w-[calc(100vw-20px)] max-h-[calc(100vh-50px)] flex flex-col">
      <DialogTitle class="flex flex-col gap-1">
        <span class="text-muted-foreground text-sm">{{ item.customerName }}</span>
        <span class="text-lg font-semibold">Registrer innbetaling</span>
      </DialogTitle>
      <DialogDescription class="flex items-start gap-2 text-sm text-slate-600">
        <Landmark :size="16" class="shrink-0 mt-0.5" />
        <span>
          Bruk dette når pengene har kommet inn på klubbens egen konto uten at de er registrert
          automatisk, for eksempel når betaleren har glemt KID. Fakturaen oppdateres som om
          innbetalingen hadde kommet med KID.
        </span>
      </DialogDescription>
      <div class="flex flex-col gap-4 py-2 text-sm">
        <div class="text-slate-600">
          {{ item.description }} · Gjenstår {{ formatKroner(remainingKroner) }}
        </div>
        <ClubAccountHistoryList
          v-if="item.paidKroner > 0"
          :history="history"
          :is-loading="historyLoading"
        />
        <div class="flex flex-col gap-1">
          <Label for="register-payment-amount">Beløp (kr)</Label>
          <Input id="register-payment-amount" v-model="amount" type="number" step="0.01" min="0.01" />
        </div>
        <div class="flex flex-col gap-1">
          <Label for="register-payment-date">Innbetalt dato</Label>
          <Input id="register-payment-date" v-model="paidAt" type="date" />
        </div>
        <div class="flex flex-col gap-1">
          <Label for="register-payment-from">Betalerens kontonummer (valgfritt)</Label>
          <Input
            id="register-payment-from"
            v-model="fromAccount"
            placeholder="1234 56 78901"
            inputmode="numeric"
          />
          <span class="text-xs text-slate-500">Gjør det enklere å betale tilbake senere.</span>
        </div>
        <div class="flex flex-col gap-1">
          <Label for="register-payment-note">Notat (valgfritt)</Label>
          <Input id="register-payment-note" v-model="note" maxlength="200" />
        </div>
      </div>
      <DialogFooter>
        <DialogClose as-child>
          <Button variant="ghost">Avbryt</Button>
        </DialogClose>
        <DataStateButton :data-state="dataState" :disabled="!isValid" @click.prevent.stop="submit">
          Registrer innbetaling
        </DataStateButton>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
